import { useRef, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImageIcon, Loader2 } from "lucide-react";

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

// Hoisted so React does not remount (and blur) the buttons on each keystroke.
function ToolbarBtn({ onAction, label, children }) {
  return (
    <button
      type="button"
      title={label}
      data-testid={`rte-${label}`}
      onMouseDown={(e) => e.preventDefault()} // keep caret/selection in the editor
      onClick={onAction}
      className="rounded-md p-2 text-muted transition-colors hover:bg-white/10 hover:text-white"
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const savedRange = useRef(null);
  const [uploading, setUploading] = useState(false);
  const inited = useRef(false);

  useEffect(() => {
    if (ref.current && !inited.current) {
      try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch { /* noop */ }
      ref.current.innerHTML = value && value.trim() ? value : "<p><br></p>";
      inited.current = true;
    }
  }, [value]);

  const emit = () => onChange(ref.current.innerHTML);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRange.current = sel.getRangeAt(0);
  };
  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const cmd = (command, arg = null) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    saveSelection();
    const url = window.prompt("Link URL (https://…)");
    ref.current.focus();
    restoreSelection();
    if (url) cmd("createLink", url);
  };

  const pickImage = () => fileRef.current?.click();

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      ref.current.focus();
      document.execCommand("insertHTML", false, `<img src="${url}" alt="" />`);
      emit();
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02]">
      <div className="flex flex-wrap items-center gap-1 border-b border-white/10 p-2">
        <ToolbarBtn onAction={() => cmd("bold")} label="bold"><Bold className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onAction={() => cmd("italic")} label="italic"><Italic className="h-4 w-4" /></ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <ToolbarBtn onAction={() => cmd("formatBlock", "<h2>")} label="h2"><Heading2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onAction={() => cmd("formatBlock", "<h3>")} label="h3"><Heading3 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onAction={() => cmd("formatBlock", "<blockquote>")} label="quote"><Quote className="h-4 w-4" /></ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <ToolbarBtn onAction={() => cmd("insertUnorderedList")} label="ul"><List className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onAction={() => cmd("insertOrderedList")} label="ol"><ListOrdered className="h-4 w-4" /></ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-white/10" />
        <ToolbarBtn onAction={addLink} label="link"><Link2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onAction={pickImage} label="image">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}</ToolbarBtn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="rte-image-input" />
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        data-testid="rte-content"
        className="prose-editor min-h-[220px] max-h-[420px] overflow-y-auto px-4 py-3 text-sm text-white outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
}
